import { FC, useState } from 'react';
import Button, { ButtonVariant } from 'src/components/atoms/Button';
import { IProof } from 'src/ts/interfaces';
import { IListCheckEntry, IListCheckRulings } from 'src/ts/interfaces/listCheck';
import { listCheckChip, partitionListChecks } from '../../helpers/listCheckHelpers';
import { CandidateXrayState } from '../../hooks/useCandidateXray';
import { ListCheckStatus } from '../../hooks/useListCheckRulings';
import { XrayAnalysisState } from '../../hooks/useXrayAnalysis';
import {
  ListCheckGroup,
  ListCheckGroupLabel,
  ListCheckRow,
  ListCheckRowName,
  ListChecksCenteredState,
  ListChecksLayout,
  ListChecksMaster,
  ListChecksPanelContainer
} from '../../styles';
import ListCheckChip from './ListCheckChip';
import ListCheckDetailPane from './ListCheckDetailPane';

interface ListChecksPanelProps {
  status: ListCheckStatus;
  data: IListCheckRulings | null;
  error: string | null;
  onRetry: () => void;
  currentProof?: IProof;
  documentXray?: XrayAnalysisState;
  candidateXray?: CandidateXrayState;
  isSubmitting: boolean;
  onSelect: (checkKey: string, rowId: string) => void;
  onProviderMismatch: (checkKey: string, rowId: string, note?: string) => void;
  onNoneOfThese: (checkKey: string, note?: string) => void;
  onFraudTrendAnswer: (checkKey: string, edited: boolean, note?: string) => void;
  onUndo: (checkKey: string) => void;
}

const ListChecksPanel: FC<ListChecksPanelProps> = ({
  status,
  data,
  error,
  onRetry,
  currentProof,
  documentXray,
  candidateXray,
  isSubmitting,
  onSelect,
  onProviderMismatch,
  onNoneOfThese,
  onFraudTrendAnswer,
  onUndo
}) => {
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  if (status === 'loading' || status === 'idle') {
    return (
      <ListChecksCenteredState data-testid="list-checks-loading">
        <span>Loading list checks…</span>
      </ListChecksCenteredState>
    );
  }

  if (status === 'error' || !data) {
    return (
      <ListChecksCenteredState data-testid="list-checks-error">
        <span>{error ?? 'List checks failed to load'}</span>
        <Button
          name="list-checks-retry"
          label="Retry"
          variant={ButtonVariant.outline}
          onClick={onRetry}
        >
          Retry
        </Button>
      </ListChecksCenteredState>
    );
  }

  if (data.rulings.length === 0) {
    return (
      <ListChecksCenteredState data-testid="list-checks-empty">
        <span>
          {data.status === null
            ? 'List checks did not run for this document.'
            : 'No checks have answered for this document yet.'}
        </span>
      </ListChecksCenteredState>
    );
  }

  const entries = data.rulings;
  const { needsReview, reviewed, cleared } = partitionListChecks(entries);
  const selected =
    entries.find((entry) => entry.check_key === selectedKey) ??
    needsReview[0] ??
    reviewed[0] ??
    cleared[0];

  const renderRow = (entry: IListCheckEntry, quiet: boolean) => {
    const isSelected = selected?.check_key === entry.check_key;
    const chip = listCheckChip(entry);
    return (
      <ListCheckRow
        key={entry.check_key}
        type="button"
        id={`list-check-row-${entry.check_key}`}
        data-testid={`list-check-row-${entry.check_key}`}
        $active={isSelected}
        $quiet={quiet}
        aria-pressed={isSelected}
        onClick={() => setSelectedKey(entry.check_key)}
      >
        <ListCheckRowName>{entry.display_name}</ListCheckRowName>
        {chip && <ListCheckChip chip={chip} />}
      </ListCheckRow>
    );
  };

  return (
    <ListChecksPanelContainer data-testid="list-checks-panel">
      <ListChecksLayout>
        <ListChecksMaster>
          {needsReview.length > 0 && (
            <ListCheckGroup data-testid="list-checks-group-needs-review">
              <ListCheckGroupLabel $needs>Needs review ({needsReview.length})</ListCheckGroupLabel>
              {needsReview.map((entry) => renderRow(entry, false))}
            </ListCheckGroup>
          )}
          {reviewed.length > 0 && (
            <ListCheckGroup data-testid="list-checks-group-reviewed">
              <ListCheckGroupLabel>Reviewed ({reviewed.length})</ListCheckGroupLabel>
              {reviewed.map((entry) => renderRow(entry, true))}
            </ListCheckGroup>
          )}
          {cleared.length > 0 && (
            <ListCheckGroup data-testid="list-checks-group-cleared">
              <ListCheckGroupLabel>Cleared ({cleared.length})</ListCheckGroupLabel>
              {cleared.map((entry) => renderRow(entry, true))}
            </ListCheckGroup>
          )}
        </ListChecksMaster>
        {selected && (
          <ListCheckDetailPane
            key={selected.check_key}
            entry={selected}
            currentProof={currentProof}
            documentXray={documentXray}
            candidateXray={candidateXray}
            isSubmitting={isSubmitting}
            canAnswer={data.status !== 'enqueued' && data.status !== 'running'}
            onSelect={(rowId) => {
              setSelectedKey(selected.check_key);
              onSelect(selected.check_key, rowId);
            }}
            onProviderMismatch={(rowId, note) => {
              setSelectedKey(selected.check_key);
              onProviderMismatch(selected.check_key, rowId, note);
            }}
            onNoneOfThese={(note) => {
              setSelectedKey(selected.check_key);
              onNoneOfThese(selected.check_key, note);
            }}
            onFraudTrendAnswer={(edited, note) => {
              setSelectedKey(selected.check_key);
              onFraudTrendAnswer(selected.check_key, edited, note);
            }}
            onUndo={() => {
              setSelectedKey(selected.check_key);
              onUndo(selected.check_key);
            }}
          />
        )}
      </ListChecksLayout>
    </ListChecksPanelContainer>
  );
};

export default ListChecksPanel;
